import { useQuery } from '@tanstack/react-query';
import { api } from './api';

export type DeliverableStatus =
  'NOT_STARTED' | 'DRAFTING' | 'INTERNAL_REVIEW' | 'SUBMITTED' | 'REVISIONS_REQUESTED' | 'ACCEPTED';

export const DELIVERABLE_ORDER: DeliverableStatus[] = [
  'NOT_STARTED',
  'DRAFTING',
  'INTERNAL_REVIEW',
  'SUBMITTED',
  'REVISIONS_REQUESTED',
  'ACCEPTED',
];

export function deliverableLabel(s: DeliverableStatus, client: string) {
  return {
    NOT_STARTED: 'Not started',
    DRAFTING: 'Drafting',
    INTERNAL_REVIEW: 'Internal review',
    SUBMITTED: `Submitted to ${client}`,
    REVISIONS_REQUESTED: 'Revisions requested',
    ACCEPTED: 'Accepted',
  }[s];
}

export interface DeliverableRow {
  id: string;
  name: string;
  aliases: string[];
  isProgramLevel: boolean;
  phase: { id: string; number: number; name: string };
  status: DeliverableStatus;
  targetDate: string | null;
  submittedAt: string | null;
  acceptedAt: string | null;
  acceptedBy: string | null;
  currentVersion: number;
  daysAwaiting: number | null;
  approver: { id: string | null; name: string; delegated: boolean } | null;
  canAccept: boolean;
  taskCount: number;
}

export interface DeliverableDetail {
  id: string;
  name: string;
  aliases: string[];
  isProgramLevel: boolean;
  phaseId: string;
  description: string | null;
  status: DeliverableStatus;
  targetDate: string | null;
  submittedAt: string | null;
  acceptedAt: string | null;
  currentVersion: number;
  reviewChecklist: Array<{ area: string; item: string }>;
  daysAwaiting: number | null;
  approver: { id: string; name: string } | null;
  approverDelegated: boolean;
  moves: DeliverableStatus[];
  isAdmin: boolean;
  events: Array<{
    id: string;
    from: DeliverableStatus | null;
    to: DeliverableStatus;
    version: number | null;
    note: string | null;
    at: string;
    actor: string;
  }>;
  comments: Array<{
    id: string;
    body: string;
    createdAt: string;
    author: { id: string; name: string };
    aiRunId: string | null;
  }>;
  tasks: Array<{ id: string; title: string; status: string; dueDate: string | null }>;
}

export const deliverablesKey = (programId: string) => ['deliverables', programId] as const;

export function useDeliverables(programId: string) {
  return useQuery({
    queryKey: deliverablesKey(programId),
    queryFn: () => api.get<DeliverableRow[]>(`/api/programs/${programId}/deliverables`),
  });
}

export function useDeliverable(programId: string, id: string) {
  return useQuery({
    queryKey: [...deliverablesKey(programId), id],
    queryFn: () => api.get<DeliverableDetail>(`/api/programs/${programId}/deliverables/${id}`),
  });
}
